import React, { useCallback, useLayoutEffect, useMemo } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorRouteProp,
} from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { validPLUProduct } from "../PLUProductEdit/validPLUProduct";
import { PLUProductComponent } from "../PLUProductComponent/PLUProductComponent";
import { PLUProductCreateMutation } from "./__generated__/PLUProductCreateMutation.graphql";
import { PLUProductCreateQuery } from "./__generated__/PLUProductCreateQuery.graphql";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import {
  NativeStackNavigationProp,
  createNativeStackNavigator,
} from "@react-navigation/native-stack";
import { RouteProp } from "@react-navigation/native";
import { useAlert } from "../../../common/Alert";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import { useNavigationHeaderOptions } from "../../../common/navigationHeaderOptions";

export const PLUProductCreateScreen = withWebModal(PLUProductCreateWithStack);

type ParamList = {
  PLUProductCreateScreenMain: {
    hidden?: boolean;
    title?: string;
    pluCode?: string;
    eanCode?: string;
    eanType?: "EAN" | "CODE39" | "CODE128";
    categoryId?: string;
    image?: { id: string; uri: string } | null;
  };
};
const Stack = createNativeStackNavigator<ParamList>();

function PLUProductCreateWithStack(props: {
  navigation: UserLoggedInNavigatorNavigationProp<"PLUProductCreate">;
  route: UserLoggedInNavigatorRouteProp<"PLUProductCreate">;
}) {
  return (
    <Stack.Navigator>
      <Stack.Screen
        component={PLUProductCreate}
        name="PLUProductCreateScreenMain"
        initialParams={{ categoryId: props.route.params.categoryId }}
      />
    </Stack.Navigator>
  );
}

type Props = {
  route: RouteProp<ParamList, "PLUProductCreateScreenMain">;
  navigation: NativeStackNavigationProp<
    ParamList,
    "PLUProductCreateScreenMain"
  >;
};

function PLUProductCreate(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerPLUProductCreate {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerPLUProductCreate(props: Props & { fetchKey: string }) {
  const { categoryId, eanCode, eanType, pluCode, title, image, hidden } =
    props.route.params;
  const data = useLazyLoadQuery<PLUProductCreateQuery>(
    graphql`
      query PLUProductCreateQuery {
        pluCategories {
          id
          color
          title
        }
      }
    `,
    {},
    { fetchKey: props.fetchKey }
  );

  const [createPLUProduct, isInFlight] = useMutation<PLUProductCreateMutation>(
    graphql`
      mutation PLUProductCreateMutation(
        $id: ID!
        $input: PLUCreateProductInput!
      ) {
        pluCategory(id: $id) {
          createProduct(input: $input) {
            id
            title
            eanCode
            eanType
            pluCode
            image {
              id
            }
            category {
              id
              products {
                ...PLUProductItem_PLUProduct
              }
            }
          }
        }
      }
    `
  );

  const Alert = useAlert();

  const onCreate = useCallback(() => {
    if (!categoryId || !title) {
      throw new Error(
        "Invalid input " + JSON.stringify({ categoryId, title, pluCode })
      );
    }
    createPLUProduct({
      variables: {
        id: categoryId,
        input: {
          eanCode,
          eanType,
          pluCode,
          title,
          active: !hidden,
          imageId: image?.id ?? undefined,
        },
      },
      updater: (store, data) => {
        if (!image || !data?.pluCategory?.createProduct?.image?.id) {
          return;
        }
        const imageId = data.pluCategory.createProduct.image.id;

        const largeImage = store.create(imageId + "largeImage", "ImageSize");
        largeImage.setValue(image.uri, "url");

        store.get(imageId)?.setLinkedRecords([largeImage], "sizes");
      },
      onError: (error) => {
        Alert.alert("Error", error.message);
        console.log("onError", error);
      },
      onCompleted: (d) => {
        props.navigation.goBack();
      },
    });
  }, [
    Alert,
    categoryId,
    createPLUProduct,
    eanCode,
    eanType,
    hidden,
    image,
    pluCode,
    props.navigation,
    title,
  ]);

  const getOptions = useNavigationHeaderOptions();

  const intl_tokens = useIntlTokens();

  useLayoutEffect(() => {
    props.navigation.setOptions(
      getOptions({
        type: "CREATE",
        onSave: onCreate,
        saveLoading: isInFlight,
        onCancel: props.navigation.goBack,
        title: intl_tokens.AddProduct,
        disabled: !validPLUProduct({
          title,
          eanCode,
          eanType,
          categoryId,
        }),
      })
    );
  }, [
    categoryId,
    eanCode,
    eanType,
    getOptions,
    intl_tokens.AddProduct,
    isInFlight,
    onCreate,
    props.navigation,
    title,
  ]);

  const selectedCategory = useMemo(() => {
    return (
      data.pluCategories.find((category) => category.id === categoryId) ?? null
    );
  }, [categoryId, data.pluCategories]);

  return data.pluCategories ? (
    <PLUProductComponent
      active={!hidden}
      setActive={(active) => props.navigation.setParams({ hidden: !active })}
      image={
        image
          ? {
              type: "local",
              uri: image.uri,
            }
          : null
      }
      setImage={(image) => props.navigation.setParams({ image })}
      title={title ?? ""}
      eanCode={eanCode ?? ""}
      pluCode={pluCode ?? ""}
      selectedCategory={selectedCategory}
      setTitle={(title) => props.navigation.setParams({ title })}
      setEanCode={(eanCode, eanType) =>
        props.navigation.setParams({ eanCode, eanType })
      }
      setPluCode={(pluCode) => props.navigation.setParams({ pluCode })}
      setSelectedCategory={(categoryId) =>
        props.navigation.setParams({ categoryId })
      }
      categories={data.pluCategories}
    />
  ) : null;
}
